import { FC, HTMLAttributes, SyntheticEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Autocomplete, AutocompleteRenderInputParams, Box, Button, CircularProgress, Grid, IconButton, Link, ListSubheader, Paper, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import { IComboBoxIds, IOption } from '../../../../interfaces/comboBox';
import styles from './styles';
import { useNavigate } from 'react-router-dom';
import { SelectedClientContext } from '../../../../context/selectedClientContext';
import { getBBPeriodRequest, getOptionLabel, isOptionEqualToValue, formatDate, checkRate, checkRatesByToCurrencyIds, getClientByIdRequest, getAvailableUpcDatesForCalc, getUpcToCurrencyIds, getAvailableUpcDatesForCalcForAPAging, getUpcToCurrencyIdsForAPAging, getUpcToCurrencyIdsForGlTransaction } from '../../../../utility/helper';
import { useIsFirstRender } from '../../../../utility/hooks';
import { IBBPeriod, IClient, ICollateral } from '../../../../interfaces';
import { AMERICAN_DATE_FORMAT, GET, ISO_DATE_FORMAT, NON_EXISTING } from '../../../../utility/constants';
import axiosInstance from '../../../../service/axiosInstance';
import { ineligibleSettingAPI, reportsAPI } from '../../../../service/api';
import useBbPeriods from '../../../../utility/custom-hooks/useBbPeriods';
import useExchangeRates from '../../../../utility/custom-hooks/useExchangeRates';
import LoadingBackdrop from '../../../../components/common/loading-backdrop';
import WarningModal from '../../../../components/file-import/modals/warning-modal';
import DisabledComponentsContainer from '../../../../components/common/disabled-components-container';
import { getActiveCollaterals } from '../../../../components/client-settings/tabs/tab-panel-contents/accounts-receivable';
import { getActiveCollaterals as invGetActiveCollaterals } from '../../../../components/client-settings/tabs/tab-panel-contents/inventory-settings';
import { getAsOfDates } from '../../../../api/ineligible-settings';

export interface IDialogSelectProps {
  handleClose         : () => void;
  title               : string;
  subtitle?           : string;
  selectARCollateral? : boolean;
  selectInvCollateral?: boolean;
  selectBBPeriod?     : boolean;
  selectMultipleBB?   : boolean;
  forReport?          : boolean;
  forCalculate?       : boolean;
  reportLink?         : IReportRoutes;
  calculateBB?        : (borrowerName: string, arCollateralName: string, borrowerId: number, bbPeriodId: number, arCollateralId: number, collateralType?: string) => void;
  calculateUPC?       : (selectedClient: IClient, selectedEndDate: string) => void;
  bbCalcDate?         : boolean;
  hideFileImportLoader?: () => void;
}

export type IReportRoutes = 'borrowing-base' | 'ar-ineligible' | 'ar-aging' | 'inventory' | 'roll-forward' | 'ar-compare' | 'ap-aging' | 'financial-mapping' | 'balance-sheet' | 'profit-and-loss';

const collateralAndAsOfDateSpecificReports: IReportRoutes[] = ['ap-aging', 'ar-aging', 'ar-ineligible', 'roll-forward', 'inventory', 'ar-compare'];

const noCurrencyParams: IReportRoutes[] = ['borrowing-base'];

const DialogSelect: FC<IDialogSelectProps> = (props) => {
  const navigate                                          = useNavigate();
  const { selectedClient, clients, setSelectedClient }    = useContext(SelectedClientContext);

  const {
    getBbPeriodByBorrowerId,
    getAvailableBbPeriodsInInventoryReport
  } = useBbPeriods();

  const {
    getRatesByToCurrencyIds,
  } = useExchangeRates();

  const [arCollaterals, setARCollaterals]                 = useState<IOption[]>([]);
  const [selectedARCollateral, setSelectedARCollateral]   = useState<IOption | null>(null);
  const [arCollateralInput, setARCollateralInput]         = useState<string>('')
  const [invCollaterals, setInvCollaterals]               = useState<IOption[]>([]);
  const [selectedInvCollateral, setSelectedInvCollateral] = useState<IOption | null>(null);
  const [invCollateralInput, setInvCollateralInput]       = useState<string>('')
  const [bbPeriods, setBBPeriods]                         = useState<IOption[]>([]);
  const [selectedBBPeriod, setSelectedBBPeriod]           = useState<IOption | null>(null);
  const [bbPeriodInput, setBBPeriodInput]                 = useState<string>('');
  const [selectedBBPeriods, setSelectedBBPeriods]         = useState<IOption[]>([]);
  const [showVerbiage, setShowVerbiage]                   = useState<boolean>(false);
  const [isLoading, setIsLoading]                         = useState<boolean>(false);
  const [isLoadingBBPeriod, setIsLoadingBBPeriod]         = useState<boolean>(false);
  const [isLoadingOptions, setIsLoadingOptions]           = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen]                     = useState<boolean>(false);

  const [collaterals, setCollaterals]                     = useState<ICollateral[]>([]);
  const [selectedCollateral, setSelectedCollateral]       = useState<ICollateral | null>(null);
  const [collateralInput, setCollateralInput]             = useState<string>('');
  const [isCollateralInputLoading, setIsCollateralInputLoading] = useState(false);

  const [asOfDateInput, setAsOfDateInput]                 = useState<string>('');
  const [selectedAsOfDate, setSelectedAsOfDate]           = useState<IBBPeriod | null>(null);
  const [asOfDates, setAsOfDates]                         = useState<IBBPeriod[]>([]);
  const [isAsOfDatesLoading, setIsAsOfDatesLoading]       = useState(false);

  const isFirstRender                                     = useIsFirstRender();
  const [childClient, setChildClient]                     = useState<IClient | null>(null);
  const isUltimateParent                                  = useMemo(() => Boolean(selectedClient?.parentClient), [selectedClient]);
  const isChildClient                                     = useMemo(() => Boolean(selectedClient?.parentClientFk), [selectedClient]);

  /**
   * This function is responsible for initializing data and options based on the selected client record.
   * It fetches relevant data for the calculation/report based on the selected client.
   */
  useEffect(() => {
    if (!isUltimateParent) {
      // Check if current selected client is a child of the parent
      if (isChildClient) {
        setChildClient(selectedClient);
        const parentClient = clients?.find(client => client.recordId === selectedClient?.parentClientFk);
        setSelectedClient(parentClient ?? null);
      } else {
        if (selectedClient === null || selectedClient.recordId === NON_EXISTING) { return; }
        getCollaterals();
      }
    } else {
      getOptionsForUpc();
    }

    return () => {
      // return the original client if child client is stored
      if (childClient) {
        setSelectedClient(childClient);
      }
    }
  }, [selectedClient, clients, childClient]);

  /**
   * This function handles the selection of an AR collateral and triggers actions based on the selection.
   * It fetches additional data related to the selected AR collateral.
   */
  useEffect(() => {
    if (selectedClient != null && selectedCollateral != null) {
      loadAsOfDates();
    }
  }, [selectedCollateral])

  const loadAsOfDates = async () => {
    setIsAsOfDatesLoading(true);
    const loadedAsOfDates = await getAsOfDates(selectedClient as Required<IClient>, selectedCollateral as ICollateral);
    setAsOfDates(loadedAsOfDates);
    setIsAsOfDatesLoading(false);
    return loadedAsOfDates;
  };

  /**
   * This function retrieves the list of collaterals for the selected client.
   * If there are no collaterals, it sets the selected collateral to null and clears any loading state.
   */
  const getCollaterals = async () => {
    setIsCollateralInputLoading(true);
    // formik.resetForm(); // to clear initial previous errors
    const nonNullSelectedClient = selectedClient as Required<IClient>;

    const arCollaterals = await getActiveCollaterals(nonNullSelectedClient.recordId);
    const invCollaterals = await invGetActiveCollaterals(nonNullSelectedClient.recordId);

    const collaterals: ICollateral[] = []
    arCollaterals.forEach(collateral => {
      collaterals.push({...collateral, collateralType: "AR", invCollateralName: ""});
    });

    invCollaterals.forEach(collateral => {
      collaterals.push({
        ...collateral,
        borrowerId: collateral.borrowerFk,
        collateralType: "INV", 
        arCollateralName: "",
        collateralId: ""
      });
    })

    setCollaterals(collaterals);
    if (collaterals.length === 0) {
      setSelectedCollateral(null);
      setIsCollateralInputLoading(false);
      return;
    }

    const collateral = collaterals.find(collateral => collateral.default) ?? collaterals[0];
    setSelectedCollateral(collateral);
    setIsCollateralInputLoading(false);
  };

  /**
   * This function gets the options for the dropdowns when the selected client is an ultimate parent.
   */
  const getOptionsForUpc = async () => {
    setIsLoadingOptions(true);
    await handleUpcBBPeriods(selectedClient?.recordId as number);
    setIsLoadingOptions(false);
  };

  /**
   * This function is responsible for handling the BBPeriod selection in UPC.
   * It initiates data fetching and loading states for BBPeriod options for the UPC.
   * @param borrowerId The record id of the Ultimate Parent Client
   */
  const handleUpcBBPeriods = async (borrowerId: number) => {
    if (props.selectBBPeriod && props.forCalculate) {
      const hasSettingAndRule = await hasUpcIneligibleSettingsAndRule(borrowerId);
      if (hasSettingAndRule) {
        const options: IOption[] | undefined = await getAvailableUpcDatesForCalc(borrowerId);
        setBBPeriods(options ?? []);
      }
      setShowVerbiage(!hasSettingAndRule);
    }
    if ((props.selectBBPeriod || props.selectMultipleBB) && props.forReport) {
      const options: IOption[] | undefined = await getAvailableUpcDatesForReport(borrowerId, props.reportLink as IReportRoutes);
      setBBPeriods(options ?? []);
    }
  };

  /**
   * This function gets the available as of dates for reports.
   * @param borrowerId The record id of the Ultimate Parent Client.
   */
  const getAvailableUpcDatesForReport = async (borrowerId: number, reportLink: IReportRoutes) => {
    if (reportLink === 'roll-forward') {
      return await getDatesForRollForwardReport(borrowerId);
    }
    if (reportLink === 'ap-aging') {
      return await getAvailableUpcDatesForCalcForAPAging(borrowerId);
    }
    return await getDatesForArIneligibleReport(borrowerId);
  };

  /**
   * This function gets the available as of dates for AR Ineligible Report.
   * @param borrowerId The record id of the Ultimate Parent Client.
   * @returns The options for the available as of dates.
   */
  const getDatesForArIneligibleReport = async (borrowerId: number) => {
    try {
      const response = await axiosInstance.request({
        url: reportsAPI.arIneligibleReport.GET_AVAILABLE_UPC_DATES,
        method: GET,
        params: { parentClientId: borrowerId }
      });

      const endDates: string[] = response.data;
      const bbPeriodOpts: IOption[] = endDates.map((endDate, index) => ({
        recordId: parseInt(formatDate(endDate, 'YYYYMMDD')),
        label: formatDate(endDate, AMERICAN_DATE_FORMAT),
        default: index === 0
      }))
      return bbPeriodOpts;
    } catch (error) {
      console.log('GET AVAILABLE UPC AS OF DATES FOR AR INELIGIBLE REPORT ERROR: ', error);
    }
  }

  /**
   * This function gets the available as of dates for Roll Forward Report.
   * @param borrowerId The record id of the Ultimate Parent Client.
   * @returns The options for the available as of dates.
   */
  const getDatesForRollForwardReport = async (borrowerId: number) => {
    try {
      const response = await axiosInstance.request({
        url: reportsAPI.rollForwardReport.GET_AVAILABLE_UPC_DATES,
        method: GET,
        params: { parentClientId: borrowerId }
      });
      const endDates: string[] = response.data;
      const bbPeriodOpts: IOption[] = endDates.map((endDate, index) => ({
        recordId: parseInt(formatDate(endDate, 'YYYYMMDD')),
        label: formatDate(endDate, AMERICAN_DATE_FORMAT),
        default: index === 0
      }))
      return bbPeriodOpts;
    } catch (error) {
      console.log('GET AVAILABLE UPC AS OF DATES FOR ROLL FORWARD REPORT ERROR: ', error);
    }
  }

  /**
   * This function defines the paper component used for rendering dropdowns.
   *
   * @param props The props for the paper component.
   * @returns The paper component wrapped around the provided children.
   */
  const paperComponent = useCallback((props: HTMLAttributes<HTMLElement>) => (<Paper sx={styles.dropdown}>{props.children}</Paper>), []);

  /**
   * This function handles the action to proceed, either for generating a report or calculating ineligibles.
   * It invokes the appropriate action based on the context.
   */
  const handleProceed = useCallback(async () => {
    const isARCollateralAndAsOfDateSpecific = props.reportLink && collateralAndAsOfDateSpecificReports.includes(props.reportLink);

    if (isARCollateralAndAsOfDateSpecific) {
      const rate = await hasRateExistence();
      if (rate) {
        handleNavigate();
        return;
      }
    }

    if (props.forReport && !isARCollateralAndAsOfDateSpecific) {
      handleNavigate();
      return;
    }

    if (props.forCalculate) {
      const rate = await hasRateExistence();
      
      if (rate) { 
        handleCalculate();
        props.hideFileImportLoader?.();
        return;
      }
    }
    
    setIsModalOpen(true);
  }, [selectedClient, selectedARCollateral, selectedBBPeriod, selectedInvCollateral, selectedBBPeriods, selectedAsOfDate]);

  /**
   * This function checks the existence of a rate for the selected client, AR collateral, and BBPeriod.
   * If a rate exists or multiple BBPeriods are selected, it proceeds with the action.
   */
  const hasRateExistence = async () => {
    const nonNullSelectedClient = selectedClient as Required<IClient>
    const nonNullSelectedBBPeriod = selectedBBPeriod as Required<IOption>;

    if (!isUltimateParent && props.reportLink === 'ar-compare') {
      const nonNullSelectedARCollateral = selectedARCollateral as Required<IOption>;
      let hasRate = false;
      for (const bbPeriod of selectedBBPeriods) {
        const rate = await checkRate(nonNullSelectedARCollateral.recordId, nonNullSelectedClient.recordId, bbPeriod.label);
        hasRate = Boolean(rate);
      }
      return hasRate;
    } else if (!isUltimateParent && selectedCollateral) { // independent
      const nonNullSelectedCollateral = selectedCollateral as Required<ICollateral>
      const nonNullSelectedAsOfDate = selectedAsOfDate as Required<IBBPeriod>;
      const rate = await checkRate(nonNullSelectedCollateral.recordId, nonNullSelectedClient.recordId, formatDate(nonNullSelectedAsOfDate.endDate, AMERICAN_DATE_FORMAT), selectedCollateral.collateralType);
      console.log("ind:rate::",rate);
      
      return Boolean(rate);
    } else if (!isUltimateParent && selectedInvCollateral) {
      const borrowerId = nonNullSelectedClient.recordId;
      const borrowerCurrencyId = parseInt(nonNullSelectedClient.currencyId);
      const bbPeriod = formatDate(nonNullSelectedBBPeriod.label, 'YYYYMMDD');
      const collateralCurrencyId = parseInt(selectedInvCollateral.currencyId ?? '0');
      const collateralCurrencyIds = [collateralCurrencyId];

      if (collateralCurrencyId === borrowerCurrencyId) {
        return true
      } else {
        const rates = await getRatesByToCurrencyIds(borrowerId, borrowerCurrencyId, bbPeriod, collateralCurrencyIds);

        const collateralRate = rates.find(rate =>
          rate.toCurrencyId === collateralCurrencyId &&
          rate.fromCurrencyId === borrowerCurrencyId
        );

        return Boolean(collateralRate);
      }
    } else if (isUltimateParent && props.reportLink === 'ar-compare') {
      let hasRate = false;
      for (const bbPeriod of selectedBBPeriods) {
        const asOfDate = formatDate(bbPeriod.label, 'YYYYMMDD');
        const toCurrencyIds = await getToCurrencyIds(asOfDate);

        const response = await getClientByIdRequest(nonNullSelectedClient.recordId);

        if (response) {
          const rate = await checkRatesByToCurrencyIds(
            nonNullSelectedClient.recordId,
            parseInt(response.data.currencyId),
            toCurrencyIds ?? [],
            asOfDate
          );

          hasRate = Boolean(rate);
        } else {
          hasRate = false;
        }
      }
      return hasRate;
    } else {
      if (!selectedAsOfDate?.endDate) return;
      const asOfDate = formatDate(selectedAsOfDate?.endDate, 'YYYYMMDD');
      const toCurrencyIds = await getToCurrencyIds(asOfDate);

      const response = await getClientByIdRequest(nonNullSelectedClient.recordId);

      if (response) {
        const rate = await checkRatesByToCurrencyIds(
          nonNullSelectedClient.recordId,
          parseInt(response.data.currencyId),
          toCurrencyIds ?? [],
          asOfDate
        );

        return Boolean(rate);
      } else return false;
    }
  };

  /**
   * This function gets the currency ids of the collaterals based on the transactions.
   * @param asOfDate The as of date of the transactions.
   * @returns An array of the currency ids of collaterals.
   */
  const getToCurrencyIds = async (asOfDate: string) => {
    if (props.reportLink === 'ap-aging') {
      return await getUpcToCurrencyIdsForAPAging(selectedClient?.recordId as number, asOfDate);
    } else if (props.reportLink === 'roll-forward') {
      return await getUpcToCurrencyIdsForGlTransaction(selectedClient?.recordId as number, asOfDate);
    } else {
      return await getUpcToCurrencyIds(selectedClient?.recordId as number, asOfDate);
    }
  }

  /**
   * This function checks whether there are ineligible settings and rules for the parent client id.
   * @param borrowerId The record id of the Ultimate Parent Client.
   * @returns A boolean value whether there are ineligible settings and rules for the parent client id.
   */
  const hasUpcIneligibleSettingsAndRule = async (borrowerId: number) => {
    try {
      const response = await axiosInstance.request({
        url: ineligibleSettingAPI.HAS_UPC_SETTINGS_AND_RULE,
        method: GET,
        params: { parentClientId: borrowerId }
      });
      const hasUpcIneligibleSettingsAndRule: boolean = response.data;
      return hasUpcIneligibleSettingsAndRule;
    } catch (error) {
      console.log('HAS UPC INELIGIBLE SETTINGS AND RULE ERROR: ', error);
      return false;
    }
  }

  /**
   * This function retrieves the IDs of selected BBPeriods and joins them into a comma-separated string.
   *
   * @returns A comma-separated string of selected BBPeriod IDs.
   */
  const getSelectedBBPeriodIds = () => {
    return selectedBBPeriods.map((period: IOption) => period.recordId).join(',');
  };

  /**
   * This function constructs the report link based on selected criteria and report type.
   * It includes query parameters for the selected client, AR collateral, BBPeriod, and additional options.
   *
   * @returns A promise that resolves to the constructed report link.
   */
  const getReportLink = async () => {
    const main = `/reports/${props.reportLink}?clientId=${selectedClient?.recordId}`
    const queryParams: string[] = [];

    if (selectedARCollateral) queryParams.push(`arCollateralId=${selectedARCollateral?.recordId}`);
    if (selectedInvCollateral) queryParams.push(`invCollateralId=${selectedInvCollateral?.recordId}`);
    if (props.bbCalcDate) {
      setIsLoadingBBPeriod(true);
      try {
        const response = await getBBPeriodRequest(parseInt(localStorage.getItem('selectedClientRecordId') ?? '0'));
        const fetchedBBPeriods: IBBPeriod[] = response.data.content;

        const sortedPeriodsByLatest = [...fetchedBBPeriods].sort((curr, prev) => {
          return new Date(prev.endDate).getTime() - new Date(curr.endDate).getTime();
        });
        queryParams.push(`bbPeriodId=${sortedPeriodsByLatest[0].recordId}`);
      } catch (error) {
        console.log('ERROR fetching BB PERIOD BY BORROWER ID: ', error);
      } finally {
        setIsLoadingBBPeriod(false);
      }

      queryParams.push(`calcDate=${formatDate((selectedBBPeriod as IOption).label, ISO_DATE_FORMAT)}`); 
    }
    if (selectedBBPeriod && props.reportLink !== 'borrowing-base') {
      queryParams.push(`bbPeriodId=${selectedBBPeriod?.recordId}`);
      queryParams.push(`endDate=${formatDate(selectedBBPeriod?.label, 'YYYYMMDD')}`);
    }
    if (selectedBBPeriods.length > 1) queryParams.push(`bbPeriodIds=${getSelectedBBPeriodIds()}`);

    if (props.reportLink && !noCurrencyParams.includes(props.reportLink)) {
      queryParams.push(`currencyId=${selectedClient?.currencyId}`);
    };

    const params: string = queryParams.join('&');
    return main.concat('&', params);
  };

  /**
   * This function generates the JSX for a dropdown component based on its type.
   *
   * @param type The type of the dropdown.
   * @returns The JSX element representing the dropdown.
   */
  const getDropdown = (type: IComboBoxIds) => {
    return (
      <Box sx={styles.dropdownContainer}>
        <Typography
          tabIndex={0}
          component='label'
          htmlFor={`${type}-dropdown`}
          sx={styles.dropdownLabel}
        >
          {getLabelByType(type)}
        </Typography>
        {getAutocompleteComponent(type)}
      </Box>
    )
  };

  const getCategoryName = (collateralType: string) => {
    if (collateralType === 'AR') return 'Accounts Receivable';
    else if (collateralType === 'INV') return 'Inventory';
    else return collateralType;
  }

  /**
   * This function generates the Autocomplete component based on types and if for UPC.
   * @param type The type of the dropdown.
   * @returns The JSX element representing the dropdown.
   */
  const getAutocompleteComponent = (type: IComboBoxIds) => {
    const states = getSetStatesByType(type);

    if (isUltimateParent && type === 'arCollateral') {
      return (
        <Autocomplete
          id={`${type}-dropdown`}
          disablePortal
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={isOptionEqualToValue}
          options={[{ recordId: 0, label: 'All', default: true }]}
          defaultValue={{ recordId: 0, label: 'All', default: true }}
          disabled={true}
          {...getRenderInput(type)}
          PaperComponent={paperComponent}
          size='small'
          componentsProps={{
            popupIndicator: { 'aria-label':'Dropdown icon',tabIndex: 0 },
            clearIndicator:{'aria-label':'Clear icon', tabIndex: 0}}
          }
          sx={styles.dropdown}
        />
      )
    } else if (type === 'bbPeriod' && props.selectMultipleBB) {
      return (
        <Autocomplete
          multiple
          id={`${type}-dropdown`}
          disablePortal
          getOptionLabel={getOptionLabel}
          limitTags={1}
          disableCloseOnSelect
          isOptionEqualToValue={isOptionEqualToValue}
          options={states.options}
          value={selectedBBPeriods}
          loading={isLoadingOptions}
          inputValue={states.input}
          {...getRenderInput(type)}
          PaperComponent={paperComponent}
          size='small'
          componentsProps={{
            popupIndicator: { 'aria-label':'Dropdown icon',tabIndex: 0 },
            clearIndicator:{'aria-label':'Clear icon', tabIndex: 0}}
          }
          {...getMultipleChange()}
          sx={styles.dropdown}
        />
      )
    } else if (type === 'bbPeriod') {
      return (
        <Autocomplete
          id={`${type}-dropdown`}
          blurOnSelect
          disablePortal
          getOptionLabel={(asOfDates) => formatDate(asOfDates.endDate, AMERICAN_DATE_FORMAT)}
          inputValue={asOfDateInput}
          options={asOfDates}
          value={selectedAsOfDate}
          size='small'
          fullWidth
          sx={styles.dropdown}
          disabled={selectedClient === null}
          loading={isAsOfDatesLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={'Please Select'}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    { isAsOfDatesLoading ? <CircularProgress color='inherit' size={15} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
          onInputChange={(_event, newInputValue) => setAsOfDateInput(newInputValue)}
          onChange={(_event, newValue: IBBPeriod | null) => setSelectedAsOfDate(newValue)}
          aria-label='Collateral Dropdown'
          componentsProps={{clearIndicator:{'aria-label':'Remove the selected item'}}}
        />
      )
    } else {
      return (
        <Autocomplete
          id={`${type}-dropdown`}
          blurOnSelect
          disablePortal
          getOptionLabel={(collateral) => collateral.collateralType == "AR" ? collateral.arCollateralName : collateral.invCollateralName}
          inputValue={collateralInput}
          options={collaterals}
          value={selectedCollateral}
          size='small'
          fullWidth
          sx={styles.dropdown}
          disabled={selectedClient === null}
          loading={isCollateralInputLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={'Please Select'}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    { isCollateralInputLoading ? <CircularProgress color='inherit' size={15} /> : null }
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
          onInputChange={(_event, newInputValue) => setCollateralInput(newInputValue)}
          onChange={(_event, newValue: ICollateral | null) => {
            setSelectedCollateral(newValue);
            setSelectedAsOfDate(null);
          }}
          aria-label='Collateral Dropdown'
          componentsProps={{
            popupIndicator: { 'aria-label':'Dropdown icon', tabIndex: 0 },
            clearIndicator: { 'aria-label':'Clear icon', tabIndex: 0}
          }}
          groupBy={option => option.collateralType}
          renderGroup={(params) => (
            <li key={params.key}>
              <ListSubheader sx={styles.groupCategory}>{getCategoryName(params.group)}</ListSubheader>
              <Box component='ul' sx={styles.groupItems}>{params.children}</Box>
            </li>
          )}
        />
      )
    }
  }

  /**
   * This function constructs event handlers for change and input events in multiple-select dropdown components.
   *
   * @returns Object with event handler functions.
   */
  const getMultipleChange = () => {
    return {
      onChange: (_: SyntheticEvent<Element, Event>, newVal: readonly IOption[]) => setSelectedBBPeriods([...newVal]),
      onInputChange: (_: SyntheticEvent<Element, Event>, newInput: string) => setBBPeriodInput(newInput)
    }
  };

  /**
   * This function defines the rendering of the dropdown input component.
   *
   * @param type The type of the dropdown.
   * @returns Object with renderInput function.
   */
  const getRenderInput = (type: IComboBoxIds) => {
    return {renderInput: (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        placeholder={type === 'bbPeriod' && selectedBBPeriods.length > 0 ? undefined : 'Please Select'}
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
            {type === 'bbPeriod' && isLoadingOptions ?
              <CircularProgress color='inherit' size={15} sx={styles.loadingIcon} /> : null}
              {params.InputProps.endAdornment}
            </>
          ),
        }}
      />
    )}
  };

  /**
   * This function returns the label for the given dropdown type.
   *
   * @param type The type of the dropdown.
   * @returns The label for the dropdown.
   */
  const getLabelByType = (type: IComboBoxIds) => {
    switch (type) {
      case 'arCollateral':
        return 'Select Collateral';
      case 'invCollateral':
        return 'Select Collateral';
      default:
        return 'Select As of Date';
    }
  }

  /**
   * This function returns the states and state setters for a given dropdown type.
   *
   * @param type The type of the dropdown.
   * @returns Object containing options, selected value, and input value for the dropdown.
   */
  const getSetStatesByType = (type: IComboBoxIds) => {
    switch (type) {
      case 'arCollateral':
        return {options: arCollaterals, setOptions: setARCollaterals, selected: selectedARCollateral, setSelected: setSelectedARCollateral, input: arCollateralInput, setInput: setARCollateralInput};
      case 'invCollateral':
        return {options: invCollaterals, setOptions: setInvCollaterals, selected: selectedInvCollateral, setSelected: setSelectedInvCollateral, input: invCollateralInput, setInput: setInvCollateralInput};
      default:
        return {options: bbPeriods, setOptions: setBBPeriods, selected: selectedBBPeriod, setSelected: setSelectedBBPeriod, input: bbPeriodInput, setInput: setBBPeriodInput};
    }
  };

  /**
   * This function handles navigation to the report page based on selected criteria and report type.
   * @param currencyId The currencyId of the borrower.
   */
  const handleNavigate = async () => {
    navigate(await getReportLink());
  };

  /**
   * This function handles the calculation process if the 'Calculate' button is clicked.
   * It checks if the required criteria are selected and then triggers the calculation.
   */
  const handleCalculate = () => {
    if (props.calculateBB && selectedCollateral) {
      const collateralName = selectedCollateral?.collateralType === 'INV'
        ? selectedCollateral?.invCollateralName
        : selectedCollateral.arCollateralName;

      const args = {
        borrowerName: (selectedClient as Required<IClient>).borrowerName ?? '',
        collateralName,
        borrowerId: (selectedClient as Required<IClient>).recordId ?? NON_EXISTING,
        bbPeriodId: (selectedAsOfDate as Required<IBBPeriod>).recordId,
        collateralId: (selectedCollateral as Required<ICollateral>).recordId,
      }
      props.calculateBB(args.borrowerName, args.collateralName, args.borrowerId, args.bbPeriodId, args.collateralId, selectedCollateral?.collateralType);
    }

    if (props.calculateUPC) {
      props.calculateUPC(selectedClient as IClient, `${selectedBBPeriod?.recordId}`);
    }
  };

  /**
   * This function determines if the 'Proceed' button should be disabled based on various conditions.
   *
   * @returns True if the button should be disabled, false otherwise.
   */
  const handleDisabled = () => {
    return !selectedCollateral || !selectedAsOfDate;
  };

  /**
   * This function returns an object with an onClick event handler for a specific action.
   *
   * @returns Object with an onClick function for navigation.
   */
  const handleClick = () => {
    if (isUltimateParent) {
      return {
        onClick: () => {
          props.handleClose();
          navigate(`/ineligible-settings?arCollateralId=0`);
        }
      };
    }
    if (selectedARCollateral?.recordId === undefined) { return; }
    return {
      onClick: () => {
        props.handleClose();
        navigate(`/ineligible-settings?arCollateralId=${selectedARCollateral?.recordId}`);
      }
    };
  };

  return (
    <>
      <Grid container id=':rd:' sx={styles.dialog}>
        <Grid item xs={12}>
          <Grid container justifyContent='space-between' alignItems={props.subtitle ? 'flex-start' : 'center'} sx={styles.dropdownTitleContainer}>
            <Grid item>
              <Typography tabIndex={0} id='dialog-title' variant='h6' component='h1'>{props.title}</Typography>
              {props.subtitle && <Typography tabIndex={0} id='dialog-subtitle' variant='subtitle2' component='h2'>{props.subtitle}</Typography>}
            </Grid>
            <Grid item>
              <IconButton onClick={props.handleClose} aria-label='Close icon'><CloseIcon fontSize='inherit' /></IconButton>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sx={styles.dialogBody}>
          {props.forCalculate && showVerbiage && 
            <Box sx={styles.warningMessage}>
              <Alert severity='info' variant='outlined' color='warning' icon={<InfoIcon sx={styles.infoIcon} />} sx={styles.info}>
                <Typography tabIndex={0} variant='subtitle2' component='h6' sx={styles.infoMessage}>
                  Please set up your Ineligible Settings prior to calculating the Ineligible.
                  Click <Link sx={styles.ineligibleLink} {...handleClick()}>here</Link> to go to the Ineligible Settings page.
                </Typography>
              </Alert>
            </Box>}
          {isUltimateParent && getDropdown(('arCollateral'))}
          {props.selectARCollateral && getDropdown('arCollateral')}
          {props.selectInvCollateral && getDropdown('invCollateral')}
          {(props.selectBBPeriod || props.selectMultipleBB) && getDropdown('bbPeriod')}
        </Grid>
        <Grid item xs={12} marginTop={2}>
          <Grid container justifyContent='flex-end' gap={1}>
            <Button
              variant='outlined'
              size='small'
              disableElevation
              sx={styles.cancelButton}
              onClick={props.handleClose}
            >
              Cancel
            </Button>
            <DisabledComponentsContainer isDisabled={handleDisabled()}>
              <Button
                variant='contained'
                size='small'
                disableElevation
                disabled={handleDisabled()}
                aria-label={handleDisabled() ? 'Proceed button disabled' : 'Proceed'}
                sx={styles.proceedButton}
                onClick={handleProceed}
              >
                Proceed
              </Button>
            </DisabledComponentsContainer>
          </Grid>
        </Grid>
        <LoadingBackdrop
          isLoading={isLoadingBBPeriod}
        />
      </Grid>
      <WarningModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={() => navigate(`/clients/${selectedClient?.recordId}/settings/exchangeRate`)}
        noDataIncluded
        issueType='error'
        issueMessages={['There is no exchange rate yet for the selected \'As of Date\' and collateral currency. Click Proceed to visit Exchange Rate page']}
      />
    </>
  )
}
export default DialogSelect;